// react
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

// api
import { useDeleteSensorMutation } from '@/api/recalc/endpoints/sensorsApiSlice';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

interface Sensor {
  SensorId: string;
  Name?: string;
  GatewayId?: string;
}

interface DeleteSensorDialogProps {
  open: boolean;
  sensor: Sensor | null;
  onClose: () => void;
  /** Called after a successful delete, e.g. to navigate away. */
  onDeleted?: (sensor: Sensor) => void;
}

const DeleteSensorDialog = ({
  open,
  sensor,
  onClose,
  onDeleted,
}: DeleteSensorDialogProps) => {
  const { t } = useTranslation('units-gateways-sensors');
  const dispatch = useDispatch();
  const [deleteSensor, { isLoading: isDeleting }] = useDeleteSensorMutation();

  const handleConfirm = async () => {
    if (!sensor?.SensorId) return;

    const result = await deleteSensor(sensor.SensorId);

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message:
            t('sensors.sensorDeletedSuccess', {
              name: sensor.Name || sensor.SensorId,
            }) ||
            `Sensor "${sensor.Name || sensor.SensorId}" deleted successfully`,
          severity: 'success',
        }),
      );

      onClose();
      onDeleted?.(sensor);
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('sensors.deleteSensor', { defaultValue: 'Delete Sensor' })}
      </DialogTitle>
      <DialogContent dividers>
        <Typography>{t('sensors.deleteConfirmation')}</Typography>
        <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
          {sensor?.Name || sensor?.GatewayId}
        </Typography>
        {sensor?.GatewayId && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('sensors.sensorId')}: {sensor.SensorId}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isDeleting}>
          {t('common.cancel', { defaultValue: 'Cancel' })}
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={isDeleting}
        >
          {isDeleting
            ? t('common.deleting', { defaultValue: 'Deleting...' })
            : t('common.delete', { defaultValue: 'Delete' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteSensorDialog;
